import React, { useEffect, useMemo, useState } from 'react';
import { apiDelete, apiGet, apiPost, apiPut } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { CalendarClock, Clock, Edit3, Loader2, Plus, Shield, Trash2 } from 'lucide-react';

const emptyForm = {
  title: '',
  description: '',
  planned_time: '',
  event_phase: '',
  duration_minutes: 15,
  notes: '',
  secret: false,
};

const phases = [
  '',
  'Før middag',
  'Efter hovedret',
  'Efter dessert',
  'Senere på aftenen',
];

function toInputTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('da-DK', {
    timeZone: 'Europe/Copenhagen', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(date).replace('.', ':');
}

function toApiDate(time) {
  if (!time) return '';
  return `2027-02-27T${time}:00+01:00`;
}

function formatDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleString('da-DK', {
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
  });
}

export default function EntertainmentPage() {
  const { toast } = useToast();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [overlaps, setOverlaps] = useState([]);

  const sortedEntries = useMemo(
    () => [...entries].sort((a, b) => new Date(a.planned_at) - new Date(b.planned_at)),
    [entries],
  );

  const load = () => {
    setLoading(true);
    apiGet('/api/guest-entertainment')
      .then((res) => setEntries(res.data.entries || []))
      .catch((err) => {
        toast({ title: 'Indslag kunne ikke hentes', description: err.response?.data?.detail || 'Prøv igen.', variant: 'destructive' });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateForm = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const startCreate = () => {
    setEditing(null);
    setOverlaps([]);
    setForm(emptyForm);
  };

  const startEdit = (entry) => {
    setEditing(entry);
    setOverlaps([]);
    setForm({
      title: entry.title || '',
      description: entry.description || '',
      planned_time: toInputTime(entry.planned_at),
      event_phase: entry.event_phase || '',
      duration_minutes: entry.duration_minutes || 15,
      notes: entry.notes || '',
      secret: !!entry.secret,
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    const payload = { ...form, planned_at: toApiDate(form.planned_time), duration_minutes: Number(form.duration_minutes) };
    delete payload.planned_time;
    try {
      const res = editing
        ? await apiPut(`/api/guest-entertainment/${editing.id}`, payload)
        : await apiPost('/api/guest-entertainment', payload);
      setOverlaps(res.data.overlaps || []);
      toast({ title: editing ? 'Indslag opdateret' : 'Indslag oprettet' });
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast({ title: 'Indslaget kunne ikke gemmes', description: err.response?.data?.detail || 'Prøv igen.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (entry) => {
    if (!entry?.id) return;
    if (!window.confirm(`Slet "${entry.title}"?`)) return;
    try {
      await apiDelete(`/api/guest-entertainment/${entry.id}`);
      setEntries((current) => current.filter((item) => item.id !== entry.id));
      toast({ title: 'Indslag slettet' });
    } catch (err) {
      toast({ title: 'Indslaget kunne ikke slettes', description: err.response?.data?.detail || 'Prøv igen.', variant: 'destructive' });
    }
  };

  return (
    <div className="mx-auto max-w-6xl animate-fade-in" data-testid="guest-entertainment-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-50" style={{ fontFamily: "'Outfit', sans-serif" }}>Indslag & underholdning</h1>
        <p className="mt-1 max-w-2xl text-sm text-slate-400">Koordinér taler, quizzer, Kahoots og overraskelser med de andre gæster uden at afsløre dem for værten.</p>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[420px_1fr]">
        <section className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-5">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-100">
              <Plus className="h-5 w-5 text-blue-400" />
              {editing ? 'Rediger indslag' : 'Opret indslag'}
            </h2>
            {editing && (
              <Button type="button" variant="outline" onClick={startCreate} className="border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800">
                Ny
              </Button>
            )}
          </div>

          <form onSubmit={submit} className="space-y-4">
            <Field label="Titel">
              <input value={form.title} onChange={(e) => updateForm('title', e.target.value)} maxLength={120} required
                className="w-full rounded-md border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm text-slate-100 outline-none focus:border-blue-500" />
            </Field>
            <Field label="Beskrivelse">
              <textarea value={form.description} onChange={(e) => updateForm('description', e.target.value)} rows={4}
                className="w-full resize-none rounded-md border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm text-slate-100 outline-none focus:border-blue-500" />
            </Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Klokkeslæt (HH)">
                <input type="time" value={form.planned_time} onChange={(e) => updateForm('planned_time', e.target.value)} required step="60"
                  className="w-full rounded-md border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm text-slate-100 outline-none focus:border-blue-500" />
              </Field>
              <Field label="Varighed (minutter)">
                <input type="number" min="1" max="240" value={form.duration_minutes} onChange={(e) => updateForm('duration_minutes', e.target.value)} required
                  className="w-full rounded-md border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm text-slate-100 outline-none focus:border-blue-500" />
              </Field>
            </div>
            <Field label="Placering i programmet">
              <select value={form.event_phase} onChange={(e) => updateForm('event_phase', e.target.value)}
                className="w-full rounded-md border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm text-slate-100 outline-none focus:border-blue-500">
                {phases.map((phase) => <option key={phase} value={phase}>{phase || 'Ikke valgt'}</option>)}
              </select>
            </Field>
            <Field label="Noter til andre gæster">
              <textarea value={form.notes} onChange={(e) => updateForm('notes', e.target.value)} rows={3}
                className="w-full resize-none rounded-md border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm text-slate-100 outline-none focus:border-blue-500" />
            </Field>
            <label className="flex items-start gap-3 rounded-lg border border-slate-700 bg-slate-900/40 p-3 text-sm text-slate-300">
              <input type="checkbox" checked={form.secret} onChange={(e) => updateForm('secret', e.target.checked)} className="mt-1" />
              <span>
                <span className="block font-medium text-slate-100">Hemmelig overraskelse</span>
                Andre gæster ser kun, at tidspunktet er reserveret.
              </span>
            </label>
            <Button type="submit" disabled={saving} className="w-full bg-blue-600 text-white hover:bg-blue-500">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
              {editing ? 'Gem ændringer' : 'Opret indslag'}
            </Button>
          </form>

          {overlaps.length > 0 && (
            <div className="mt-4 rounded-lg border border-amber-500/20 bg-amber-500/10 p-3">
              <p className="mb-2 text-sm font-medium text-amber-200">Der er allerede planlagt noget omkring dette tidspunkt:</p>
              <div className="space-y-1 text-xs text-amber-100">
                {overlaps.map((entry) => (
                  <p key={entry.id}>{formatDate(entry.planned_at)} · {entry.title} · ca. {entry.duration_minutes} min.</p>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className="rounded-xl border border-slate-700/50 bg-slate-900/60">
          <div className="border-b border-slate-800 px-4 py-3">
            <h2 className="text-sm font-semibold text-slate-100">Fælles tidslinje</h2>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-14 text-slate-400">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Indlæser indslag...
            </div>
          ) : sortedEntries.length === 0 ? (
            <div className="py-14 text-center text-sm text-slate-500">Ingen indslag er planlagt endnu.</div>
          ) : (
            <div className="divide-y divide-slate-800">
              {sortedEntries.map((entry) => (
                <div key={entry.id} className="p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-blue-500/10 px-2.5 py-1 text-xs font-medium text-blue-300">{formatDate(entry.planned_at)}</span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-2.5 py-1 text-xs text-slate-400">
                          <Clock className="h-3 w-3" /> ca. {entry.duration_minutes} min.
                        </span>
                        {entry.secret && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-purple-500/10 px-2.5 py-1 text-xs text-purple-300">
                            <Shield className="h-3 w-3" /> Hemmelig
                          </span>
                        )}
                      </div>
                      <h3 className="text-base font-semibold text-slate-100">{entry.title}</h3>
                      <p className="mt-1 text-sm text-slate-400">{entry.description}</p>
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                        <span>{entry.is_redacted ? 'Reserveret' : `Oprettet af ${entry.creator_name || 'gæst'}`}</span>
                        {entry.event_phase && <span>{entry.event_phase}</span>}
                      </div>
                      {entry.notes && <p className="mt-3 rounded-lg bg-slate-800/60 p-3 text-xs text-slate-400">{entry.notes}</p>}
                    </div>
                    {entry.can_edit && (
                      <div className="flex w-full gap-2 sm:w-auto">
                        <Button type="button" variant="outline" size="sm" onClick={() => startEdit(entry)}
                          className="flex-1 border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 sm:flex-none">
                          <Edit3 className="h-4 w-4" /> Rediger
                        </Button>
                        <Button type="button" variant="destructive" size="sm" onClick={() => remove(entry)}
                          className="flex-1 bg-red-600 text-white hover:bg-red-500 sm:flex-none">
                          <Trash2 className="h-4 w-4" /> Slet
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm text-slate-400">{label}</span>
      {children}
    </label>
  );
}
